import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStethoscope,
  faMapMarkerAlt,
  faPhone,
  faClock,
  faCalendarPlus,
  faTimes,
  faCalendarAlt,
  faCheckCircle,
  faSpinner,
  faCommentMedical,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "./PatientPortal.module.css";
import ChatWindow from "./ChatWindow";
import "react-toastify/dist/ReactToastify.css";

const DoctorCard = ({ doctor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [slotId, setSlotId] = useState("");
  const [disease, setDisease] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSlots = async () => {
      if (doctor && appointmentDate) {
        setLoadingSlots(true);
        try {
          const response = await axios.get(
            `/api/appointment/slots?doctorId=${doctor._id}&date=${appointmentDate}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSlots(response.data.slots || []);
        } catch (error) {
          toast.error("Failed to load clinical slots");
        } finally {
          setLoadingSlots(false);
        }
      } else {
        setSlots([]);
        setSlotId("");
      }
    };
    fetchSlots();
  }, [doctor, appointmentDate, token]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!slotId) {
      toast.error("Please select an available time window.");
      return;
    }
    const patientId = localStorage.getItem("userId");
    const payload = {
      patient: patientId,
      doctor: doctor._id,
      slotId: slotId,
      disease: disease,
    };
    try {
      await axios.post("/api/appointment/book", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Consultation booked successfully!");
      setIsModalOpen(false);
      // Revamp workflow: Redirect immediately to Appointments page to view live status!
      navigate("/myappointment");
    } catch (error) {
      toast.error("Error confirming booking. Please try again.");
      console.error("Booking error:", error);
    }
  };

  function formatTimeToAMPM(time) {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    let period = "AM";
    let formattedHours = parseInt(hours, 10);
    if (formattedHours >= 12) {
      period = "PM";
      if (formattedHours > 12) {
        formattedHours -= 12;
      }
    }
    return `${formattedHours}:${minutes} ${period}`;
  }

  const fallbackImage =
    "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=500&q=80";

  return (
    <div className={styles.docCard}>
      {/* Photo Showcase */}
      <div className={styles.docPhotoBox}>
        <img
          src={doctor.profile || fallbackImage}
          alt={`Dr. ${doctor.firstName || ""} ${doctor.lastName || ""}`}
          className={styles.docPhoto}
          onError={(e) => {
            e.target.src = fallbackImage;
          }}
        />
        <div className={styles.docSpecialtyPill}>
          <FontAwesomeIcon icon={faStethoscope} />
          <span>{doctor.specialty || "Physiotherapy Specialist"}</span>
        </div>
      </div>

      {/* Card Details Body */}
      <div className={styles.docContent}>
        <div>
          <h3 className={styles.docName}>
            Dr. {doctor.firstName || ""} {doctor.lastName || ""}
          </h3>
          {doctor.about && (
            <p className={styles.docAbout} style={{ marginTop: "6px" }}>
              {doctor.about}
            </p>
          )}
        </div>

        <div className={styles.docMetaList}>
          <div className={styles.docMetaRow}>
            <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.docMetaIcon} />
            <span>{doctor.clinicLocation || "Sector 54, Gurugram"}</span>
          </div>

          <div className={styles.docMetaRow}>
            <FontAwesomeIcon icon={faPhone} className={styles.docMetaIcon} />
            <span>9024989935</span>
          </div>

          <div className={styles.docMetaRow}>
            <FontAwesomeIcon icon={faClock} className={styles.docMetaIcon} />
            <span>{doctor.workingHours || "Mon - Sat: 9:00 AM - 8:00 PM"}</span>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "auto", width: "100%" }}>
          <button
            type="button"
            onClick={() => {
              setAppointmentDate("");
              setSlotId("");
              setDisease("");
              setIsModalOpen(true);
            }}
            className={styles.docBookBtn}
            style={{ flex: 1, marginTop: 0 }}
          >
            <span>Book Consultation</span>
          </button>

          <button
            type="button"
            onClick={() => setIsChatOpen(true)}
            className={styles.docBookBtn}
            style={{ flex: 1, marginTop: 0, background: "#0f172a", boxShadow: "0 4px 15px rgba(15, 23, 42, 0.25)" }}
          >
            <span>Chat</span>
          </button>
        </div>
      </div>

      {/* ─── ULTRA-SMOOTH GLASS BOOKING MODAL (TELEPORTED TO BODY) ───── */}
      {isModalOpen &&
        createPortal(
          <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
            <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "1.75rem",
                }}
              >
                <div>
                  <h2 style={{ fontSize: "1.5rem", fontWeight: 850, color: "#0f172a", margin: 0, letterSpacing: "-0.02em" }}>
                    Schedule Consultation
                  </h2>
                  <p style={{ fontSize: "0.9rem", color: "#64748b", margin: "4px 0 0 0", fontWeight: 550 }}>
                    With Dr. {doctor.firstName} {doctor.lastName}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    color: "#475569",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1rem",
                  }}
                  aria-label="Close modal"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleBookingSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.35rem" }}>
                {/* Date Selection */}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                  <label style={{ fontSize: "0.88rem", fontWeight: 750, color: "#1e293b", display: "flex", alignItems: "center", gap: "8px" }}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "#2d6a4f" }} />
                    <span>Select Consultation Date</span>
                  </label>
                  <input
                    type="date"
                    name="appointmentDate"
                    value={appointmentDate}
                    onChange={(e) => {
                      setAppointmentDate(e.target.value);
                      setSlotId("");
                    }}
                    style={{
                      padding: "0.85rem 1.1rem",
                      borderRadius: "14px",
                      border: "1px solid #cbd5e1",
                      fontSize: "0.95rem",
                      color: "#0f172a",
                      fontWeight: 600,
                      outline: "none",
                      fontFamily: "inherit",
                      background: "#ffffff",
                      boxShadow: "0 2px 6px rgba(15, 23, 42, 0.02)",
                    }}
                    required
                  />
                </div>

                {/* Time Slot Selection */}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                  <label style={{ fontSize: "0.88rem", fontWeight: 750, color: "#1e293b", display: "flex", alignItems: "center", gap: "8px" }}>
                    <FontAwesomeIcon icon={faClock} style={{ color: "#2d6a4f" }} />
                    <span>Available Time Window</span>
                    {loadingSlots && <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#64748b", marginLeft: "auto" }} />}
                  </label>
                  <select
                    name="slotId"
                    value={slotId}
                    onChange={(e) => setSlotId(e.target.value)}
                    style={{
                      padding: "0.85rem 1.1rem",
                      borderRadius: "14px",
                      border: "1px solid #cbd5e1",
                      fontSize: "0.95rem",
                      color: "#0f172a",
                      fontWeight: 600,
                      outline: "none",
                      background: "#ffffff",
                      fontFamily: "inherit",
                      cursor: !appointmentDate || slots.length === 0 ? "not-allowed" : "pointer",
                    }}
                    required
                    disabled={!appointmentDate || slots.length === 0 || loadingSlots}
                  >
                    <option value="">
                      {!appointmentDate
                        ? "Please pick a date first"
                        : loadingSlots
                        ? "Checking clinical availability..."
                        : slots.length > 0
                        ? "Choose a preferred time slot"
                        : "No slots available for this date"}
                    </option>
                    {slots.map((s) => (
                      <option key={s._id} value={s._id}>
                        {formatTimeToAMPM(s.startTime)} - {formatTimeToAMPM(s.endTime)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Condition / Treatment Focus */}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                  <label style={{ fontSize: "0.88rem", fontWeight: 750, color: "#1e293b", display: "flex", alignItems: "center", gap: "8px" }}>
                    <FontAwesomeIcon icon={faStethoscope} style={{ color: "#2d6a4f" }} />
                    <span>Treatment Focus / Concern</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Chronic Sciatica, Rotator cuff stiffness, ACL rehab"
                    value={disease}
                    onChange={(e) => setDisease(e.target.value)}
                    style={{
                      padding: "0.85rem 1.1rem",
                      borderRadius: "14px",
                      border: "1px solid #cbd5e1",
                      fontSize: "0.95rem",
                      color: "#0f172a",
                      fontWeight: 600,
                      outline: "none",
                      fontFamily: "inherit",
                      background: "#ffffff",
                      boxShadow: "0 2px 6px rgba(15, 23, 42, 0.02)",
                    }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    marginTop: "0.75rem",
                    width: "100%",
                    padding: "1rem",
                    borderRadius: "16px",
                    background: "#2d6a4f",
                    color: "#ffffff",
                    border: "none",
                    fontSize: "1.02rem",
                    fontWeight: 800,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    boxShadow: "0 10px 25px rgba(45, 106, 79, 0.3)",
                    transition: "transform 0.2s ease, background 0.2s ease",
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Confirm & View Schedule</span>
                </button>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ─── CHAT WINDOW PORTAL ───────────────────────────────────────── */}
      {isChatOpen &&
        createPortal(
          <ChatWindow doctor={doctor} onClose={() => setIsChatOpen(false)} />,
          document.body
        )}
    </div>
  );
};

export default DoctorCard;
